"use client";
import { motion } from 'framer-motion';
import { useEffect, useState, useMemo } from 'react';
import useSWR from 'swr';
import Image from 'next/image';
import Link from 'next/link';
import { HeartIcon, ShieldCheckIcon, ClockIcon } from '@heroicons/react/24/outline';

const API = '/api';
const fetcher = (url: string) => fetch(url).then(r => r.json());

export default function AdultsPage(){
  const { data, error, isLoading } = useSWR(`${API}/adults`, fetcher);
  const [selectedAdult, setSelectedAdult] = useState<any>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const list = Array.isArray(data) ? data : [];
  const queens = list.filter((adult: any) => adult.sex?.toLowerCase() === 'female');
  const studs = list.filter((adult: any) => adult.sex?.toLowerCase() === 'male');
  const retirements = useMemo(() => list.filter((adult: any) => adult.status?.toLowerCase().includes('retiring')).length, [list]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  return (
    <main className="min-h-screen bg-gradient-to-br from-cream via-sand/20 to-soft-gold/30">
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-16">
        <motion.section
          className="mb-12 rounded-3xl border border-sand/25 bg-white/92 p-6 shadow-xl sm:p-8 lg:p-10"
          initial={isHydrated ? { opacity: 0, y: 24 } : {}}
          animate={isHydrated ? { opacity: 1, y: 0 } : {}}
          transition={isHydrated ? { duration: 0.6 } : {}}
        >
          <div className="flex flex-col gap-8 md:flex-row md:items-stretch md:justify-between">
            <div className="space-y-6 md:max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-soft-gold/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-soft-gold">
                Maison briefing
              </div>
              <div className="space-y-3">
                <h1 className="font-heading text-3xl font-bold text-warm-brown sm:text-4xl md:text-5xl">LINDY Queens & Studs</h1>
                <p className="text-sm text-warm-brown/75 sm:text-base">
                  Balinese queens and studs curated for calm temperaments, flowing coats, and generational health. Preview legacy companions or arrange retirement conversations immediately.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                {[
                  {
                    label: 'Queens in residence',
                    value: queens.length || 'Awaiting',
                    icon: HeartIcon,
                    tone: 'bg-sand/12 border-sand/30',
                  },
                  {
                    label: 'Stud partners',
                    value: studs.length || 'Awaiting',
                    icon: ShieldCheckIcon,
                    tone: 'bg-sand/12 border-sand/30',
                  },
                  {
                    label: 'Retirements',
                    value: retirements > 0 ? `${retirements} upcoming` : 'By consultation',
                    icon: ClockIcon,
                    tone: 'bg-soft-gold/15 border-soft-gold/30',
                  },
                ].map(({ label, value, icon: Icon, tone }) => (
                  <div key={label} className={`rounded-2xl border ${tone} p-4 shadow-sm`}>
                    <div className="mb-2 flex items-center gap-2 text-soft-gold">
                      <Icon className="h-5 w-5" />
                      <span className="text-[0.7rem] uppercase tracking-[0.2em] font-semibold">{label}</span>
                    </div>
                    <p className="text-lg font-semibold text-warm-brown">{value}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Link
                  href="/waiting-list"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-soft-gold px-6 py-3 text-sm font-semibold text-warm-brown transition-transform hover:-translate-y-0.5 hover:bg-soft-gold/90"
                >
                  Request updates
                </Link>
                <Link
                  href="/kittens"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-soft-gold/40 px-6 py-3 text-sm font-semibold text-soft-gold transition-colors hover:bg-soft-gold/10"
                >
                  View nursery
                </Link>
                <Link
                  href="/process"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-sand/30 px-6 py-3 text-sm font-semibold text-warm-brown/70 transition-colors hover:bg-sand/15"
                >
                  Adoption philosophy
                </Link>
              </div>
            </div>
            <div className="flex-1 rounded-3xl border border-sand/25 bg-cream/60 p-6 shadow-inner">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-soft-gold">Stewardship ethos</p>
              <div className="mt-4 space-y-3 text-sm text-warm-brown/70">
                {[
                  'Private suites with panoramic perches and climate-balanced grooming ateliers.',
                  'Pairings planned for temperament harmony and genetic vitality.',
                  'Retirement placements crafted with bespoke consultations and transition guides.',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <span className="mt-1 h-2 w-2 flex-none rounded-full bg-soft-gold" />
                    <p>{item}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 rounded-2xl border border-soft-gold/40 bg-white/85 p-4 text-xs text-warm-brown/65 sm:text-sm">
                Legacy companions depart with full veterinary dossiers and lifetime breeder support.
              </div>
            </div>
          </div>
        </motion.section>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-16"
          initial={isHydrated ? { opacity: 0, y: 20 } : {}}
          animate={isHydrated ? { opacity: 1, y: 0 } : {}}
          transition={isHydrated ? { duration: 0.6, delay: 0.1 } : {}}
        >
          {[
            { title: 'Curated Lineage', detail: 'Pedigrees traced through grand champions with genetic screenings for longevity.' },
            { title: 'Mindful Conditioning', detail: 'Daily coat therapy, enrichment play, and social immersion create serene temperaments.' },
            { title: 'Artisan Pairings', detail: 'Each match is orchestrated to balance conformation, personality, and color artistry.' }
          ].map((item) => (
            <div key={item.title} className="bg-white/80 backdrop-blur rounded-3xl border border-sand/20 p-6 shadow-sm">
              <h3 className="font-heading text-lg text-warm-brown mb-2">{item.title}</h3>
              <p className="text-sm text-warm-brown/70 leading-relaxed">{item.detail}</p>
            </div>
          ))}
        </motion.div>

        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 animate-pulse">
                <div className="w-full h-64 bg-gray-200 rounded-2xl mb-4"></div>
                <div className="h-6 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded mb-4"></div>
              </div>
            ))}
          </div>
        )}

        {error && (
          <motion.div
            className="text-center py-16"
            initial={isHydrated ? { opacity: 0 } : {}}
            animate={isHydrated ? { opacity: 1 } : {}}
          >
            <p className="text-red-500 text-lg">Failed to load adult cats. Please try again later.</p>
          </motion.div>
        )}

        {list.length > 0 && (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial={isHydrated ? { opacity: 0 } : {}}
            animate={isHydrated ? { opacity: 1 } : {}}
            transition={isHydrated ? { duration: 0.6, delay: 0.2 } : {}}
          >
            {list.map((adult: any, index: number) => (
              <motion.div
                key={adult.id}
                className="group bg-white/90 backdrop-blur-sm rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
                initial={isHydrated ? { opacity: 0, y: 20 } : {}}
                animate={isHydrated ? { opacity: 1, y: 0 } : {}}
                transition={isHydrated ? { duration: 0.5, delay: index * 0.1 } : {}}
                onClick={() => setSelectedAdult(adult)}
                whileHover={isHydrated ? { y: -5 } : {}}
              >
                <div className="relative overflow-hidden">
                  <Image
                    src={adult.main_photo || '/placeholder-cat.jpg'}
                    alt={adult.name}
                    width={400}
                    height={300}
                    className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-4 left-4 right-4 transform translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3">
                      <p className="text-warm-brown font-medium text-sm">
                        {adult.sex === 'female' ? '👑 Queen' : '🤴 Stud'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-heading text-xl font-semibold text-warm-brown">
                      {adult.name}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      adult.sex === 'female'
                        ? 'bg-rose-beige text-warm-brown'
                        : 'bg-soft-gold text-white'
                    }`}>
                      {adult.sex === 'female' ? 'Queen' : 'Stud'}
                    </span>
                  </div>

                  {adult.pedigree && (
                    <p className="text-warm-brown/60 text-sm mb-3 line-clamp-2">
                      {adult.pedigree}
                    </p>
                  )}

                  {adult.desc && (
                    <p className="text-warm-brown/70 text-sm line-clamp-3 mb-4">
                      {adult.desc}
                    </p>
                  )}

                  <button onClick={() => setSelectedAdult(adult)} className="w-full border-2 border-soft-gold text-soft-gold py-3 px-4 rounded-xl font-medium hover:bg-soft-gold hover:text-white transition-all">
                    View Details
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {list.length === 0 && !isLoading && (
          <motion.div
            className="text-center py-16"
            initial={isHydrated ? { opacity: 0 } : {}}
            animate={isHydrated ? { opacity: 1 } : {}}
          >
            <p className="text-warm-brown/60 text-lg mb-4">Our LINDY family is currently in repose between litters.</p>
            <p className="text-warm-brown/60 text-sm">Reach out for bespoke updates on upcoming pairings and scheduled retirements.</p>
          </motion.div>
        )}
      </div>

      {/* Adult Details Modal */}
      {selectedAdult && (
        <motion.div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          initial={isHydrated ? { opacity: 0 } : {}}
          animate={isHydrated ? { opacity: 1 } : {}}
          exit={isHydrated ? { opacity: 0 } : {}}
          onClick={() => setSelectedAdult(null)}
        >
          <motion.div
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            initial={isHydrated ? { scale: 0.9, opacity: 0 } : {}}
            animate={isHydrated ? { scale: 1, opacity: 1 } : {}}
            exit={isHydrated ? { scale: 0.9, opacity: 0 } : {}}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <Image
                src={selectedAdult.main_photo || '/placeholder-cat.jpg'}
                alt={selectedAdult.name}
                width={800}
                height={400}
                className="w-full h-64 md:h-80 object-cover rounded-t-3xl"
              />
              <button
                onClick={() => setSelectedAdult(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-warm-brown hover:bg-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-3xl font-bold text-warm-brown">
                  {selectedAdult.name}
                </h2>
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                  selectedAdult.sex === 'female'
                    ? 'bg-rose-beige text-warm-brown'
                    : 'bg-soft-gold text-white'
                }`}>
                  {selectedAdult.sex === 'female' ? '👑 LINDY Queen' : '🤴 LINDY Stud'}
                </span>
              </div>

              {selectedAdult.pedigree && (
                <div className="mb-6">
                  <h3 className="font-heading text-lg font-semibold text-warm-brown mb-2">Pedigree</h3>
                  <p className="text-warm-brown/70 bg-cream/50 p-4 rounded-xl">
                    {selectedAdult.pedigree}
                  </p>
                </div>
              )}

              {selectedAdult.desc && (
                <div className="mb-6">
                  <h3 className="font-heading text-lg font-semibold text-warm-brown mb-2">About</h3>
                  <p className="text-warm-brown/70 leading-relaxed">
                    {selectedAdult.desc}
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link
                  href={`/adults/application?adultId=${selectedAdult.id}&adultName=${encodeURIComponent(selectedAdult.name)}&adultImage=${encodeURIComponent(selectedAdult.main_photo || '/placeholder-cat.jpg')}&adultSex=${encodeURIComponent(selectedAdult.sex)}&adultType=${encodeURIComponent(selectedAdult.sex === 'female' ? 'Queen' : 'Stud')}&adultPedigree=${encodeURIComponent(selectedAdult.pedigree || 'LINDY lineage')}&adultPurpose=${encodeURIComponent(selectedAdult.status?.toLowerCase().includes('retiring') ? 'Retirement Placement' : 'Breeding Rights Inquiry')}&adultDescription=${encodeURIComponent(selectedAdult.desc || `${selectedAdult.name} is a cherished member of our LINDY family with excellent temperament and health.`)}`}
                  className="flex-1 border-2 border-soft-gold text-soft-gold py-3 px-6 rounded-xl font-semibold text-center hover:bg-soft-gold hover:text-white transition-all"
                >
                  Apply Now
                </Link>
                <Link
                  href="/kittens"
                  className="flex-1 border-2 border-soft-gold text-soft-gold py-3 px-6 rounded-xl font-medium text-center hover:bg-soft-gold hover:text-white transition-all"
                >
                  View Available Kittens
                </Link>
                <Link
                  href="/waiting-list"
                  className="flex-1 border-2 border-soft-gold text-soft-gold py-3 px-6 rounded-xl font-medium text-center hover:bg-soft-gold hover:text-white transition-all"
                >
                  Join Waiting List
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </main>
  );
}
